import React, { useEffect, useState } from 'react';
import { FileCheck, BookOpen, CreditCard, GraduationCap, Users, ArrowUpRight, TrendingUp } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { TestPrepEnrollment, TestPrepMockExam } from '../../types';

interface TestPrepDashboardProps {
    onNavigate: (tab: string) => void;
}

const TestPrepDashboard: React.FC<TestPrepDashboardProps> = ({ onNavigate }) => {
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState([
        { label: 'Aktif Öğrenciler', value: '0', icon: Users, color: 'blue', change: '...' },
        { label: 'Bu Ay Kayıt', value: '0', icon: BookOpen, color: 'green', change: '...' },
        { label: 'Bekleyen Ödemeler', value: '0 ₺', icon: CreditCard, color: 'red', change: '...' },
        { label: 'Yaklaşan Sınavlar', value: '0', icon: FileCheck, color: 'purple', change: '...' },
    ]);
    const [recentEnrollments, setRecentEnrollments] = useState<TestPrepEnrollment[]>([]);
    const [upcomingExams, setUpcomingExams] = useState<TestPrepMockExam[]>([]);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            // 1. Stats: Active Students
            const { count: activeStudentsCount } = await supabase
                .from('testprep_students')
                .select('*', { count: 'exact', head: true })
                .eq('status', 'active');

            // 2. Stats: New Registrations (This Month)
            const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();
            const { count: newStudentsCount } = await supabase
                .from('testprep_students')
                .select('*', { count: 'exact', head: true })
                .gte('created_at', firstDayOfMonth);

            // 3. Stats: Pending Payments
            const { data: payments } = await supabase
                .from('testprep_payments')
                .select('installments, currency')
                .eq('status', 'active');

            let totalPending = 0;
            if (payments) {
                payments.forEach(p => {
                    const pendingInstallments = (p.installments as any[]).filter(i => i.status === 'pending');
                    const pendingAmount = pendingInstallments.reduce((sum, i) => sum + Number(i.amount), 0);

                    // Currency conversion to EUR
                    if (p.currency === 'EUR') totalPending += pendingAmount;
                    else if (p.currency === 'USD') totalPending += pendingAmount * 0.95; // Approx USD to EUR
                    else totalPending += pendingAmount / 38; // Approx TL to EUR
                });
            }

            // 4. Stats: Upcoming Exams (From today)
            const todayStr = new Date().toISOString().split('T')[0];
            const { count: upcomingExamsCount } = await supabase
                .from('testprep_mock_exams')
                .select('*', { count: 'exact', head: true })
                .gte('exam_date', todayStr);

            // Update Stats State
            setStats([
                { label: 'Aktif Öğrenciler', value: String(activeStudentsCount || 0), icon: Users, color: 'blue', change: 'Güncel' },
                { label: 'Bu Ay Kayıt', value: String(newStudentsCount || 0), icon: BookOpen, color: 'green', change: 'Bu Ay' },
                { label: 'Bekleyen Ödemeler', value: `€${totalPending.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`, icon: CreditCard, color: 'red', change: 'Tahmini' },
                { label: 'Yaklaşan Sınavlar', value: String(upcomingExamsCount || 0), icon: FileCheck, color: 'purple', change: 'Gelecek' },
            ]);

            // 5. Recent Enrollments
            const { data: enrollments } = await supabase
                .from('testprep_enrollments')
                .select(`
                    *,
                    student:testprep_students (
                        first_name,
                        last_name
                    )
                `)
                .order('created_at', { ascending: false })
                .limit(5);

            if (enrollments) setRecentEnrollments(enrollments);

            // 6. Upcoming Mock Exams (List)
            const { data: exams } = await supabase
                .from('testprep_mock_exams')
                .select('*')
                .gte('exam_date', todayStr)
                .order('exam_date', { ascending: true })
                .limit(3);

            if (exams) setUpcomingExams(exams);

        } catch (error) {
            console.error('Dashboard veri çekme hatası:', error);
        } finally {
            setLoading(false);
        }
    };

    const quickActions = [
        { id: 'mock-exams', label: 'Deneme Sınavı Gir', icon: FileCheck, color: 'bg-purple-100 text-purple-600' },
        { id: 'course-enrollments', label: 'Yeni Kurs Kaydı', icon: BookOpen, color: 'bg-green-100 text-green-600' },
        { id: 'payments', label: 'Ödeme Al', icon: CreditCard, color: 'bg-blue-100 text-blue-600' },
        { id: 'private-lessons', label: 'Ders Programı', icon: GraduationCap, color: 'bg-orange-100 text-orange-600' },
        { id: 'teacher-payments', label: 'Hoca Ödemeleri', icon: Users, color: 'bg-indigo-100 text-indigo-600' },
        { id: 'revenue', label: 'Gelir Takibi', icon: TrendingUp, color: 'bg-teal-100 text-teal-600' },
    ];

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Test Prep Paneli</h1>
                    <p className="text-gray-600 dark:text-gray-400">Test hazırlık birimi genel durum özeti</p>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {stats.map((stat, index) => {
                    const Icon = stat.icon;
                    return (
                        <div key={index} className="bg-white dark:bg-gray-900 p-6 rounded-xl border border-gray-200 dark:border-gray-800">
                            <div className="flex items-center justify-between mb-4">
                                <div className={`p-3 rounded-lg bg-${stat.color}-100 dark:bg-${stat.color}-900/20`}>
                                    <Icon className={`w-6 h-6 text-${stat.color}-600 dark:text-${stat.color}-400`} />
                                </div>
                                <span className={`text-sm font-medium text-gray-600`}>
                                    {stat.change}
                                </span>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{stat.value}</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{stat.label}</p>
                        </div>
                    );
                })}
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {quickActions.map((action) => {
                    const Icon = action.icon;
                    return (
                        <button
                            key={action.id}
                            onClick={() => onNavigate(action.id)}
                            className="flex items-center p-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl hover:shadow-md transition-all group text-left"
                        >
                            <div className={`p-3 rounded-lg mr-4 ${action.color} group-hover:scale-110 transition-transform`}>
                                <Icon className="w-5 h-5" />
                            </div>
                            <div>
                                <span className="block font-medium text-gray-900 dark:text-white">{action.label}</span>
                                <span className="text-xs text-gray-500 flex items-center mt-1">
                                    Git <ArrowUpRight className="w-3 h-3 ml-1" />
                                </span>
                            </div>
                        </button>
                    );
                })}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Enrollments */}
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">Son Kurs Kayıtları</h3>
                        <button onClick={() => onNavigate('course-enrollments')} className="text-sm text-blue-600 hover:text-blue-700 font-medium">Tümünü Gör</button>
                    </div>
                    <div className="space-y-4">
                        {loading ? (
                            <div className="text-center py-4 text-gray-500">Yükleniyor...</div>
                        ) : recentEnrollments.length > 0 ? (
                            recentEnrollments.map((item) => (
                                <div key={item.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                                    <div className="flex items-center space-x-3">
                                        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center text-blue-600 font-bold">
                                            {item.student?.first_name.charAt(0)}{item.student?.last_name.charAt(0)}
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-gray-900 dark:text-white">
                                                {item.student?.first_name} {item.student?.last_name}
                                            </h4>
                                            <p className="text-sm text-gray-500">{item.course_name} - {item.term}</p>
                                        </div>
                                    </div>
                                    <span className="text-sm text-green-600 bg-green-100 dark:bg-green-900/20 px-2 py-1 rounded">
                                        {item.status || 'Aktif'}
                                    </span>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-4 text-gray-500">Kayıt bulunamadı.</div>
                        )}
                    </div>
                </div>

                {/* Upcoming Mock Exams */}
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">Yaklaşan Deneme Sınavları</h3>
                        <button onClick={() => onNavigate('mock-exams')} className="text-sm text-blue-600 hover:text-blue-700 font-medium">Programı Gör</button>
                    </div>
                    <div className="space-y-4">
                        {loading ? (
                            <div className="text-center py-4 text-gray-500">Yükleniyor...</div>
                        ) : upcomingExams.length > 0 ? (
                            upcomingExams.map((exam) => {
                                const date = new Date(exam.exam_date);
                                const day = date.getDate();
                                const month = date.toLocaleString('tr-TR', { month: 'short' }).toUpperCase();

                                return (
                                    <div key={exam.id} className="flex items-center p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border-l-4 border-purple-500">
                                        <div className="mr-4 text-center min-w-[3rem]">
                                            <span className="block text-xl font-bold text-gray-900 dark:text-white">{day}</span>
                                            <span className="text-xs text-gray-500">{month}</span>
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-gray-900 dark:text-white">{exam.exam_type}</h4>
                                            <p className="text-sm text-gray-500">{exam.notes || 'Detay yok'}</p>
                                        </div>
                                    </div>
                                )
                            })
                        ) : (
                            <div className="text-center py-4 text-gray-500">Yaklaşan sınav yok.</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TestPrepDashboard;
